import { sanitizeHtml } from '@/utils/sanitizeHtml'

const sanitizeHtmlDirective = {
  mounted(el, binding) {
    updateSanitizedHtml(el, binding)
  },
  updated(el, binding) {
    updateSanitizedHtml(el, binding)
  },
}

function updateSanitizedHtml(el, binding) {
  const bindingValue = binding?.value
  const isOptionsObject = bindingValue && typeof bindingValue === 'object' && !Array.isArray(bindingValue)
  const html = isOptionsObject ? bindingValue.html : bindingValue
  const profile = isOptionsObject ? bindingValue.profile || 'richText' : 'richText'
  el.innerHTML = sanitizeHtml(html, profile)
}

// 获取元素的 transform scale 值
function getTransformScale(element) {
  const transform = window.getComputedStyle(element).transform
  if (!transform || transform === 'none') return 1
  const matrix = new DOMMatrixReadOnly(transform)
  return Math.abs(matrix.a) // m11 = scaleX
}

// 超出展示提示弹窗
const globalTooltip = {
  beforeMount: (el) => {
    el.classList.add('input-ellipsis')
  },
  mounted: function (el, binding) {
    // 解析容器参数，支持 v-globalTooltip="'.selector'" 或 v-globalTooltip="{ container: '.selector' }"
    let container = null
    const bindingValue = binding?.value
    let placement = 'bottom' // 默认在下方
    if (typeof bindingValue === 'string') {
      container = document.querySelector(bindingValue)
    } else if (bindingValue && typeof bindingValue === 'object') {
      const containerVal = bindingValue.container
      container = typeof containerVal === 'string' ? document.querySelector(containerVal) : containerVal
      if (bindingValue.placement) {
        placement = bindingValue.placement // 'top' 或 'bottom'
      }
    }
    // 嵌套层层级深入查找
    const getDeepestTextElement = (element) => {
      let current = element
      while (
        current.firstElementChild &&
        current.children.length === 1 &&
        !['INPUT', 'TEXTAREA'].includes(current.firstElementChild.tagName)
      ) {
        current = current.firstElementChild
      }
      return current
    }

    const targetElement =
      el.querySelector('input') || el.querySelector('.text') || getDeepestTextElement(el) || el
    // const targetElement = el.querySelector('input') || el.querySelector('.text') || el;
    const style = window.getComputedStyle(targetElement)
    if (style.display === 'inline' || style.display === 'inline-block') {
      targetElement.style.display = 'inline-block'
    }
    // 设置必要的样式
    targetElement.classList.add('input-ellipsis')

    // 隐藏tooltip
    const hideTooltip = () => {
      const tooltip = document.getElementById('vc-tooltip')
      if (tooltip && tooltip.parentNode) {
        tooltip.parentNode.removeChild(tooltip)
      }
    }

    // 显示tooltip
    const showTooltip = () => {
      // 先隐藏可能已存在的tooltip
      hideTooltip()

      // 获取要显示的内容和判断是否溢出
      let content = ''
      let isOverflow = false
      // const EDITABLE_SELECTOR = 'input, textarea, [contenteditable], p, span, div.editable';

      if (targetElement.tagName === 'INPUT' || targetElement.tagName === 'TEXTAREA') {
        // 对于输入框，只显示placeholder
        content = targetElement.getAttribute('placeholder') || ''

        // 创建临时span测量placeholder文本实际宽度
        const span = document.createElement('span')
        span.style.visibility = 'hidden'
        span.style.whiteSpace = 'nowrap'
        span.style.position = 'absolute'
        span.style.fontSize = window.getComputedStyle(targetElement).fontSize
        span.style.fontFamily = window.getComputedStyle(targetElement).fontFamily
        span.textContent = content
        document.body.appendChild(span)

        // 比较placeholder文本宽度和输入框可用宽度
        const inputPadding =
          parseFloat(window.getComputedStyle(targetElement).paddingLeft) +
          parseFloat(window.getComputedStyle(targetElement).paddingRight)
        const inputWidth = targetElement.clientWidth - inputPadding
        isOverflow = span.offsetWidth > inputWidth
        document.body.removeChild(span)
      } else {
        // 对于普通元素，显示textContent
        content = targetElement.textContent.trim()

        const range = document.createRange()
        range.selectNodeContents(targetElement)

        const style = window.getComputedStyle(targetElement)
        const contentWidth =
          targetElement.getBoundingClientRect().width -
          parseFloat(style.paddingLeft) -
          parseFloat(style.paddingRight)

        isOverflow =
          targetElement.scrollWidth > targetElement.clientWidth ||
          el.scrollWidth > el.clientWidth ||
          range.getBoundingClientRect().width > contentWidth + 0.01
      }

      // 检查是否需要显示tooltip
      if (!content || content === '--' || !isOverflow) return

      // 创建tooltip元素
      const tooltip = document.createElement('div')
      const spanDom = document.createElement('span')
      spanDom.setAttribute('id', 'my-triangle')
      tooltip.id = 'vc-tooltip'
      tooltip.textContent = content

      // 设置tooltip样式
      tooltip.style.cssText = `
        position: fixed;
        background: rgba(0,0,0,0.8);
        color: #fff;
        font-size: 12px;
        padding: 8px 12px;
        border-radius: 4px;
        max-width: 500px;
        z-index: 9999;
        pointer-events: none;
        transform: translateX(-50%);
        word-break: break-all;
        white-space: normal;
        box-sizing: border-box;
      `

      document.body.appendChild(tooltip)
      document.getElementById('vc-tooltip').appendChild(spanDom)

      // 计算并设置位置
      const targetRect = targetElement.getBoundingClientRect()
      if (container) {
        // 挂载到指定容器，使用 absolute 定位相对于容器计算
        // 注意：容器如有 transform scale，getBoundingClientRect 返回视口坐标，
        // 而 position: absolute 使用容器的本地坐标，需除以 scale 进行转换
        tooltip.style.position = 'absolute'
        container.appendChild(tooltip)
        document.getElementById('vc-tooltip').appendChild(spanDom)

        const scale = getTransformScale(container)
        const containerRect = container.getBoundingClientRect()
        const tooltipWidth = tooltip.offsetWidth
        const tooltipHeight = tooltip.offsetHeight

        // 将视口坐标转换为容器本地坐标
        const targetLeftLocal = (targetRect.left - containerRect.left) / scale
        const targetWidthLocal = targetRect.width / scale
        const targetBottomLocal = (targetRect.bottom - containerRect.top) / scale
        const targetTopLocal = (targetRect.top - containerRect.top) / scale
        const containerWidthLocal = containerRect.width / scale
        const containerHeightLocal = containerRect.height / scale

        let left = targetLeftLocal + targetWidthLocal / 2
        let top
        if (placement === 'top') {
          top = targetTopLocal - tooltipHeight - 5
          spanDom.setAttribute('id', 'my-triangle-bt')
        } else {
          top = targetBottomLocal + 5
        }

        // 水平边界检测
        if (left + tooltipWidth / 2 > containerWidthLocal) {
          left = containerWidthLocal - tooltipWidth / 2
        }
        if (left - tooltipWidth / 2 < 0) {
          left = tooltipWidth / 2
        }
        // 垂直边界检测：当 placement 为 top 时上方空间不足则翻转到下方
        if (placement === 'top') {
          if (top < 0) {
            top = targetBottomLocal + 5
            spanDom.setAttribute('id', 'my-triangle')
          }
        } else {
          if (top + tooltipHeight > containerHeightLocal) {
            top = targetTopLocal - tooltipHeight - 5
            spanDom.setAttribute('id', 'my-triangle-bt')
          }
        }

        tooltip.style.left = `${left}px`
        tooltip.style.top = `${top}px`
      } else {
        // 默认挂载到 body，使用 fixed 定位相对于视口计算
        tooltip.style.position = 'fixed'
        document.body.appendChild(tooltip)
        document.getElementById('vc-tooltip').appendChild(spanDom)

        const tooltipWidth = tooltip.offsetWidth
        const tooltipHeight = tooltip.offsetHeight

        let left = targetRect.left + targetRect.width / 2
        let top
        if (placement === 'top') {
          top = targetRect.top - tooltipHeight - 5
          spanDom.setAttribute('id', 'my-triangle-bt')
        } else {
          top = targetRect.bottom + 5
        }

        // 水平边界检测
        if (left + tooltipWidth / 2 > window.innerWidth) {
          left = window.innerWidth - tooltipWidth / 2
        }
        if (left - tooltipWidth / 2 < 0) {
          left = tooltipWidth / 2
        }
        // 垂直边界检测：当 placement 为 top 时上方空间不足则翻转到下方
        if (placement === 'top') {
          if (top < 0) {
            top = targetRect.bottom + 5
            spanDom.setAttribute('id', 'my-triangle')
          }
        } else {
          if (top + tooltipHeight > window.innerHeight) {
            top = targetRect.top - tooltipHeight - 5
            spanDom.setAttribute('id', 'my-triangle-bt')
          }
        }

        tooltip.style.left = `${left}px`
        tooltip.style.top = `${top}px`
      }
    }

    // 添加事件监听
    el.addEventListener('mouseenter', showTooltip)
    el.addEventListener('mouseleave', hideTooltip)
    el.addEventListener('focus', hideTooltip, true)

    // 对于输入框，当有内容时不显示tooltip
    if (targetElement.tagName === 'INPUT' || targetElement.tagName === 'TEXTAREA') {
      targetElement.addEventListener('input', () => {
        if (targetElement.value) {
          hideTooltip()
        }
      })
    }
  },
  unmounted: function () {
    // 移除tooltip
    const tooltip = document.getElementById('vc-tooltip')
    if (tooltip && tooltip.parentNode) {
      tooltip.parentNode.removeChild(tooltip)
    }
  },
}
// tooltip溢出显示
const tooltipAutoShow = {
  inserted(el, binding, vnode) {
    el.__vueOverflowTooltipMouseenter__ = function (e) {
      const defalutSilent = !!Vue.config.silent
      Vue.config.silent = true
      vnode.componentInstance.disabled = true
      if (e.target.offsetLeft + e.target.offsetWidth > e.target.offsetParent.offsetWidth) {
        vnode.componentInstance.disabled = false
      } else {
        vnode.componentInstance.disabled = true
      }
      // 处理多行省略
      if (getStyle(el, '-webkit-line-clamp') > 1 && el.scrollHeight > el.offsetHeight) {
        vnode.componentInstance.disabled = false
      }
      Vue.config.silent = defalutSilent
    }
    el.addEventListener('mouseenter', el.__vueOverflowTooltipMouseenter__)
  },
  unbind: function (el) {
    el.removeEventListener('mouseenter', el.__vueOverflowTooltipMouseenter__)
    delete el.__vueOverflowTooltipMouseenter__
  },
}

export default {
  globalTooltip: globalTooltip,
  'tooltip-auto-show': tooltipAutoShow,
  'sanitize-html': sanitizeHtmlDirective,
}
